/** Tighten line picking so hinge arcs do not steal clicks from nearby joint spheres (default threshold is huge in world units). */
export function configureRaycasterForScenePicking(raycaster) {
  raycaster.params.Line = { threshold: 0.015 };
  if (raycaster.params.Points) {
    raycaster.params.Points.threshold = 0.05;
  }
}

/**
 * Among ray hits that resolve to a joint, pick the closest along the ray.
 * When distances tie (e.g. stacked pick meshes), prefer a mesh hit over a line/arc.
 */
export function pickBestJointHit(validIntersects, findValidTarget) {
  let best = null;
  let bestDist = Infinity;
  let bestIsMesh = false;
  for (const hit of validIntersects) {
    const t = findValidTarget(hit.object);
    if (!t || t.type !== "joint") continue;
    const d = hit.distance;
    const isMesh = !!hit.object.isMesh;
    if (d < bestDist - 1e-5 || (Math.abs(d - bestDist) <= 1e-5 && isMesh && !bestIsMesh)) {
      bestDist = d;
      bestIsMesh = isMesh;
      best = t;
    }
  }
  return best;
}

export function pickJointCenterProxyHit(validIntersects, findValidTarget) {
  for (const hit of validIntersects) {
    if (!hit?.object?.userData?.isJointCenterPickProxy) continue;
    const target = findValidTarget(hit.object);
    if (target?.type === "joint") return target;
  }
  return null;
}

/** First enabled inspector priority in this order that has a ray-hit candidate wins. */
export function pickPriorityInspectTarget(candidates, flags, options = {}) {
  const {
    primaryTargetType = null,
    showVisual = true,
    showJoints = false,
    showCollision = false,
    showInertials = false,
    showSites = false,
    showConstraintMarkers = false,
  } = options;
  const anyEnabled = Object.values(flags).some(Boolean);
  const constraintCandidateKey = String(candidates.constraint?.key ?? "");
  const hasTendonCandidate = constraintCandidateKey.startsWith("tendon:");
  const hasEqualityConstraintCandidate = !!candidates.constraint && !hasTendonCandidate;
  if (!anyEnabled) return null;
  const bodyOnlyPriorityEnabled =
    !!flags.body &&
    !flags.object &&
    !flags.joint &&
    !flags.mesh &&
    !flags.visualGeom &&
    !flags.collision &&
    !flags.site &&
    !flags.inertial &&
    !flags.constraint &&
    !flags.tendon;

  const lookupByTargetType = (type) => {
    if (type === "joint" && showJoints && candidates.joint) {
      return { targetType: "joint", candidate: candidates.joint };
    }
    if (type === "camera" && candidates.camera) {
      return { targetType: "camera", candidate: candidates.camera };
    }
    if (type === "light" && candidates.light) {
      return { targetType: "light", candidate: candidates.light };
    }
    if (type === "actuator" && candidates.actuator) {
      return { targetType: "actuator", candidate: candidates.actuator };
    }
    if (type === "geom" && showCollision && candidates.geom) {
      return { targetType: "geom", candidate: candidates.geom };
    }
    if (type === "inertial" && showInertials && candidates.inertial) {
      return { targetType: "inertial", candidate: candidates.inertial };
    }
    if (type === "site" && showSites && candidates.site) {
      return { targetType: "site", candidate: candidates.site };
    }
    if (type === "mesh" && candidates.mesh) {
      if (candidates.mesh.selectionType === "geom") {
        return { targetType: "mesh", candidate: candidates.mesh };
      }
      if (showVisual) {
        return { targetType: "mesh", candidate: candidates.mesh };
      }
    }
    if (type === "body" && candidates.body) {
      return { targetType: "body", candidate: candidates.body };
    }
    if (type === "object" && candidates.object) {
      return { targetType: "object", candidate: candidates.object };
    }
    if (type === "constraint" && showConstraintMarkers && hasEqualityConstraintCandidate) {
      return { targetType: "constraint", candidate: candidates.constraint };
    }
    if (type === "tendon" && showConstraintMarkers && hasTendonCandidate) {
      return { targetType: "constraint", candidate: candidates.constraint };
    }
    return null;
  };

  // Smart default: when users keep the default "body" priority, still honor direct
  // clicks on non-visual overlays (joint/collision/inertial/site). This avoids opening
  // body panels when visibles are hidden or when overlays are isolated from visual meshes.
  if (bodyOnlyPriorityEnabled) {
    if (candidates.camera) return { targetType: "camera", candidate: candidates.camera };
    if (candidates.light) return { targetType: "light", candidate: candidates.light };
    if (candidates.actuator) return { targetType: "actuator", candidate: candidates.actuator };
    if (showConstraintMarkers && candidates.constraint) {
      return { targetType: "constraint", candidate: candidates.constraint };
    }
    const primaryPick = lookupByTargetType(primaryTargetType);
    if (primaryPick) return primaryPick;
    if (!showVisual) {
      if (showJoints && candidates.joint)
        return { targetType: "joint", candidate: candidates.joint };
      if (showCollision && candidates.geom)
        return { targetType: "geom", candidate: candidates.geom };
      if (showInertials && candidates.inertial) {
        return { targetType: "inertial", candidate: candidates.inertial };
      }
      if (showSites && candidates.site) return { targetType: "site", candidate: candidates.site };
      if (candidates.mesh) return { targetType: "mesh", candidate: candidates.mesh };
    }
  }

  if (candidates.camera) return { targetType: "camera", candidate: candidates.camera };
  if (candidates.light) return { targetType: "light", candidate: candidates.light };
  if (candidates.actuator) return { targetType: "actuator", candidate: candidates.actuator };
  if (flags.tendon && showConstraintMarkers && hasTendonCandidate) {
    return { targetType: "constraint", candidate: candidates.constraint };
  }
  if (flags.constraint && showConstraintMarkers && hasEqualityConstraintCandidate) {
    return { targetType: "constraint", candidate: candidates.constraint };
  }
  if (flags.joint && candidates.joint) return { targetType: "joint", candidate: candidates.joint };
  if (flags.collision && candidates.geom) return { targetType: "geom", candidate: candidates.geom };
  if (flags.visualGeom && candidates.mesh?.selectionType === "geom") {
    return { targetType: "mesh", candidate: candidates.mesh };
  }
  if (flags.inertial && candidates.inertial) {
    return { targetType: "inertial", candidate: candidates.inertial };
  }
  if (flags.site && candidates.site) return { targetType: "site", candidate: candidates.site };
  if (flags.mesh && candidates.mesh && candidates.mesh.selectionType !== "geom") {
    return { targetType: "mesh", candidate: candidates.mesh };
  }
  if (flags.body && candidates.body) return { targetType: "body", candidate: candidates.body };
  if (flags.object && candidates.object)
    return { targetType: "object", candidate: candidates.object };
  return null;
}

/**
 * Joint equality uses a larger sphere around the tendon sphere when pairs coincide; ray hits outer shell first.
 * Prefer any tendon marker hit along the ray so the inner tendon sphere / tendon lines stay selectable.
 */
export function preferTendonConstraintMarkerHit(intersects) {
  if (!Array.isArray(intersects) || intersects.length === 0) return null;
  const tendonHits = intersects.filter((h) =>
    String(h.object?.userData?.key ?? "").startsWith("tendon:")
  );
  if (tendonHits.length > 0) {
    tendonHits.sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));
    return tendonHits[0];
  }
  return intersects[0];
}
